import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { saveRFP } from './storageService';

const RfpPortal = () => {
  const location = useLocation();
  const prefilled = location.state || {};

  const [step, setStep] = useState(1);
  const [submittedRFP, setSubmittedRFP] = useState(null);

  const [rfpData, setRfpData] = useState({
    projectType: prefilled.prefilledDomain || 'Full-Stack SaaS / Web Application',
    techStack: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
    complianceNeeds: [],
    ndaRequired: true,
    budgetTier: prefilled.prefilledBudget || 'Custom Scope / Need Estimate',
    timeline: '1 - 3 Months',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    role: '',
    projectScope: prefilled.prefilledScope || '',
    keyDeliverables: ''
  });

  useEffect(() => {
    if (prefilled.prefilledDomain) {
      setRfpData(prev => ({
        ...prev,
        projectType: prefilled.prefilledDomain,
        budgetTier: prefilled.prefilledBudget || prev.budgetTier,
        projectScope: prefilled.prefilledScope || prev.projectScope
      }));
    }
  }, [prefilled]);

  const toggleTech = (tech) => {
    setRfpData(prev => ({
      ...prev,
      techStack: prev.techStack.includes(tech)
        ? prev.techStack.filter(t => t !== tech)
        : [...prev.techStack, tech]
    }));
  };

  const toggleCompliance = (item) => {
    setRfpData(prev => ({
      ...prev,
      complianceNeeds: prev.complianceNeeds.includes(item)
        ? prev.complianceNeeds.filter(c => c !== item)
        : [...prev.complianceNeeds, item]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const saved = saveRFP(rfpData);
    setSubmittedRFP(saved);
  };

  const handleDownloadRFP = () => {
    if (!submittedRFP) return;
    const jsonStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(submittedRFP, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", jsonStr);
    downloadAnchor.setAttribute("download", `${submittedRFP.id}_Specification.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  return (
    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '100vh', paddingTop: '3.5rem', paddingBottom: '6rem' }}>
      <div className="container-custom">
        {/* Header */}
        <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 3.5rem auto' }}>
          <div className="pill-badge pill-blue" style={{ marginBottom: '1.2rem' }}>
            📋 ENTERPRISE PROJECT SCOPING
          </div>
          <h1 style={{ fontSize: 'clamp(2.2rem, 4.2vw, 3.2rem)', marginBottom: '1rem', fontWeight: 900 }}>
            Request for Proposal <br />
            <span className="gradient-text-blue">(RFP) Portal</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', lineHeight: '1.6' }}>
            Submit your technical specifications, architecture parameters, and delivery requirements for a formal Statement of Work (SOW) from Guru&apos;s Workshop LLC.
          </p>
        </div>

        {/* Form Container */}
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="workshop-card" style={{ padding: '3rem 2.5rem', backgroundColor: '#ffffff' }}>
              {submittedRFP ? (
                /* Confirmation View */
                <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                  <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>📋✨</div>
                  <div className="pill-badge pill-emerald" style={{ marginBottom: '1rem' }}>
                    PROPOSAL RECORDED SECURELY
                  </div>
                  <h2 style={{ fontSize: '2.1rem', marginBottom: '0.6rem', color: '#0f172a' }}>
                    RFP Submitted Successfully
                  </h2>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto 1.5rem auto' }}>
                    Your project parameters have been registered with <strong>Guru&apos;s Workshop LLC</strong>.
                  </p>

                  <div style={{
                    margin: '1.5rem auto',
                    maxWidth: '400px',
                    padding: '1.1rem',
                    backgroundColor: 'var(--accent-blue-subtle)',
                    border: '1px solid var(--accent-blue-border)',
                    borderRadius: '12px',
                    fontFamily: 'monospace',
                    fontSize: '1.25rem',
                    color: 'var(--accent-blue)',
                    fontWeight: 800
                  }}>
                    {submittedRFP.id}
                  </div>

                  <div style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '14px',
                    padding: '1.6rem',
                    textAlign: 'left',
                    marginBottom: '2rem',
                    border: '1px solid var(--border-subtle)'
                  }}>
                    <h4 style={{ fontSize: '1.05rem', marginBottom: '1rem', color: '#0f172a' }}>Summary of Terms:</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', fontSize: '0.92rem' }}>
                      <div><strong style={{ color: 'var(--text-muted)' }}>Project Domain:</strong> {submittedRFP.projectType}</div>
                      <div><strong style={{ color: 'var(--text-muted)' }}>Target Budget:</strong> {submittedRFP.budgetTier}</div>
                      <div><strong style={{ color: 'var(--text-muted)' }}>Delivery Schedule:</strong> {submittedRFP.timeline}</div>
                      <div><strong style={{ color: 'var(--text-muted)' }}>Mutual NDA:</strong> {submittedRFP.ndaRequired ? 'Requested' : 'Standard'}</div>
                      <div><strong style={{ color: 'var(--text-muted)' }}>Organization:</strong> {submittedRFP.companyName || submittedRFP.contactName}</div>
                      <div><strong style={{ color: 'var(--text-muted)' }}>Contact Email:</strong> {submittedRFP.email}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button onClick={handleDownloadRFP} className="btn-outline-blue">
                      ⬇ Download Specification (JSON)
                    </button>
                    <Link to="/orders" className="btn-primary-blue">
                      View in Pipeline Portal →
                    </Link>
                  </div>
                </div>
              ) : (
                /* Multi-step form */
                <form onSubmit={handleSubmit}>
                  {/* Step indicators */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2.5rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '1.2rem' }}>
                    {[
                      { num: 1, label: '1. Domain Scope' },
                      { num: 2, label: '2. Stack & Compliance' },
                      { num: 3, label: '3. Budget & Delivery' },
                      { num: 4, label: '4. Executive Contact' }
                    ].map((s) => (
                      <div
                        key={s.num}
                        onClick={() => { if (step > s.num) setStep(s.num); }}
                        style={{
                          fontSize: '0.88rem',
                          fontWeight: step === s.num ? 800 : 500,
                          color: step === s.num ? 'var(--accent-blue)' : (step > s.num ? 'var(--accent-emerald)' : 'var(--text-muted)'),
                          cursor: step > s.num ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        {step > s.num ? '✓' : ''} {s.label}
                      </div>
                    ))}
                  </div>

                  {/* Step 1 */}
                  {step === 1 && (
                    <div>
                      <h3 style={{ fontSize: '1.35rem', marginBottom: '0.6rem' }}>1. Select Project Domain &amp; Classification</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                        What primary capability are you commissioning from Guru&apos;s Workshop LLC?
                      </p>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.8rem', marginBottom: '2rem' }}>
                        {[
                          { title: 'Full-Stack SaaS / Web Application', desc: 'React, Node, Python, cloud architectures, database design and modern UI/UX.' },
                          { title: 'DevOps & Maintenance Retainer', desc: '24/7 uptime monitoring, security patching, refactoring, and dedicated monthly hours.' },
                          { title: 'Algorithmic Engine / Intelligent Tools', desc: 'Custom quantitative models, automation workflows, mathematical engines, or data pipelines.' },
                          { title: 'Mobile Application Engineering', desc: 'Native iOS / Swift platforms or cross-platform responsive web applications.' },
                          { title: 'Enterprise Legacy Modernization', desc: 'Codebase refactoring, performance tuning, and zero-downtime cloud migration.' }
                        ].map((domain) => (
                          <div
                            key={domain.title}
                            onClick={() => setRfpData({ ...rfpData, projectType: domain.title })}
                            style={{
                              padding: '1.1rem',
                              borderRadius: '12px',
                              border: `1px solid ${rfpData.projectType === domain.title ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                              backgroundColor: rfpData.projectType === domain.title ? 'var(--accent-blue-subtle)' : '#ffffff',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.2rem', color: rfpData.projectType === domain.title ? 'var(--accent-blue)' : 'var(--text-primary)' }}>
                              {domain.title}
                            </div>
                            <div style={{ fontSize: '0.86rem', color: 'var(--text-secondary)' }}>{domain.desc}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" onClick={() => setStep(2)} className="btn-primary-blue">
                          Continue to Stack Preferences →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2 */}
                  {step === 2 && (
                    <div>
                      <h3 style={{ fontSize: '1.35rem', marginBottom: '0.6rem' }}>2. Preferred Tech Stack &amp; Compliance</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                        Select technologies you wish to standardize on or have existing infrastructure in.
                      </p>

                      <div style={{ marginBottom: '1.8rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.92rem' }}>
                          Core Technologies
                        </label>
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                          {['React', 'Node.js', 'Python', 'PostgreSQL', 'AWS Cloud', 'Google Cloud (GCP)', 'Docker / K8s', 'GraphQL', 'Swift / iOS', 'Tailwind / Vanilla CSS'].map((tech) => (
                            <button
                              type="button"
                              key={tech}
                              onClick={() => toggleTech(tech)}
                              className={`tab-btn ${rfpData.techStack.includes(tech) ? 'active' : ''}`}
                            >
                              {rfpData.techStack.includes(tech) ? '✓ ' : '+ '} {tech}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ marginBottom: '1.8rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.92rem' }}>
                          Compliance &amp; Security Requirements
                        </label>
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                          {['SOC2 Type II Ready', 'HIPAA Healthcare Standard', 'GDPR / CCPA Privacy', 'PCI-DSS Payments', 'Zero-Trust Architecture'].map((comp) => (
                            <button
                              type="button"
                              key={comp}
                              onClick={() => toggleCompliance(comp)}
                              className={`tab-btn ${rfpData.complianceNeeds.includes(comp) ? 'active' : ''}`}
                            >
                              {rfpData.complianceNeeds.includes(comp) ? '✓ ' : '+ '} {comp}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{
                        padding: '1.1rem',
                        borderRadius: '12px',
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-subtle)',
                        marginBottom: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
                            Mutual Non-Disclosure Agreement (NDA)
                          </div>
                          <div style={{ fontSize: '0.84rem', color: 'var(--text-secondary)' }}>
                            Execute mutual NDA signed by Guru&apos;s Workshop LLC prior to architecture deep-dive.
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={rfpData.ndaRequired}
                          onChange={(e) => setRfpData({ ...rfpData, ndaRequired: e.target.checked })}
                          style={{ width: '20px', height: '20px', accentColor: 'var(--accent-blue)' }}
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button type="button" onClick={() => setStep(1)} className="btn-outline-blue">
                          ← Back
                        </button>
                        <button type="button" onClick={() => setStep(3)} className="btn-primary-blue">
                          Continue to Budget &amp; Delivery →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3 */}
                  {step === 3 && (
                    <div>
                      <h3 style={{ fontSize: '1.35rem', marginBottom: '0.6rem' }}>3. Budget Allocation &amp; Delivery Schedule</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                        Transparent planning ensures we calibrate scope to achieve maximum velocity and ROI.
                      </p>

                      <div style={{ marginBottom: '1.8rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.92rem' }}>
                          Target Budget Range
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                          {[
                            'Custom Scope / Need Estimate',
                            'Personal / Small Project (< $2,500)',
                            'MVP / Early Stage ($2,500 - $10,000)',
                            'Growth Platform ($10,000 - $35,000)',
                            'Enterprise Architecture ($35,000+)',
                            'Ongoing Retainer / Maintenance'
                          ].map((budget) => (
                            <button
                              type="button"
                              key={budget}
                              onClick={() => setRfpData({ ...rfpData, budgetTier: budget })}
                              className={`tab-btn ${rfpData.budgetTier === budget ? 'active' : ''}`}
                              style={{ padding: '0.85rem', textAlign: 'center', fontWeight: 700 }}
                            >
                              {budget}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.92rem' }}>
                          Target Completion Window
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.8rem' }}>
                          {['Urgent (< 30 Days)', '1 - 3 Months', '3 - 6 Months', 'Ongoing Retainer'].map((time) => (
                            <button
                              type="button"
                              key={time}
                              onClick={() => setRfpData({ ...rfpData, timeline: time })}
                              className={`tab-btn ${rfpData.timeline === time ? 'active' : ''}`}
                              style={{ padding: '0.85rem', textAlign: 'center' }}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button type="button" onClick={() => setStep(2)} className="btn-outline-blue">
                          ← Back
                        </button>
                        <button type="button" onClick={() => setStep(4)} className="btn-primary-blue">
                          Continue to Contact Information →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 4 */}
                  {step === 4 && (
                    <div>
                      <h3 style={{ fontSize: '1.35rem', marginBottom: '0.6rem' }}>4. Organization &amp; Project Description</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                        Where should Guru&apos;s Workshop LLC send your tailored Statement of Work?
                      </p>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem', fontWeight: 600 }}>
                            Organization / Individual Name *
                          </label>
                          <input
                            type="text"
                            required
                            className="workshop-input"
                            placeholder="Acme Systems or Individual Name"
                            value={rfpData.companyName}
                            onChange={(e) => setRfpData({ ...rfpData, companyName: e.target.value })}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem', fontWeight: 600 }}>
                            Contact Person Name *
                          </label>
                          <input
                            type="text"
                            required
                            className="workshop-input"
                            placeholder="Jane Doe"
                            value={rfpData.contactName}
                            onChange={(e) => setRfpData({ ...rfpData, contactName: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem', fontWeight: 600 }}>
                            Email Address *
                          </label>
                          <input
                            type="email"
                            required
                            className="workshop-input"
                            placeholder="you@domain.com"
                            value={rfpData.email}
                            onChange={(e) => setRfpData({ ...rfpData, email: e.target.value })}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem', fontWeight: 600 }}>
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            className="workshop-input"
                            placeholder="+1 (555) 000-0000"
                            value={rfpData.phone}
                            onChange={(e) => setRfpData({ ...rfpData, phone: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem', fontWeight: 600 }}>
                          Project Scope &amp; Functional Requirements *
                        </label>
                        <textarea
                          rows="4"
                          required
                          className="workshop-input"
                          placeholder="Summarize your architecture objectives, user stories, integrations, and milestones..."
                          value={rfpData.projectScope}
                          onChange={(e) => setRfpData({ ...rfpData, projectScope: e.target.value })}
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button type="button" onClick={() => setStep(3)} className="btn-outline-blue">
                          ← Back
                        </button>
                        <button type="submit" className="btn-primary-blue" style={{ padding: '0.85rem 2.5rem' }}>
                          Submit Formal RFP →
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RfpPortal;
